import { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { profileApiService } from '../services/profileApiService';
import { apiService } from '../services/api';
import {
  OfficialApiInbox,
  BusinessProfile,
  UpdateProfileData,
  BusinessVertical,
  BusinessVerticalOptions
} from '../types/template';
import toast from 'react-hot-toast';
import { ProfilePreviewModal } from './ProfilePreviewModal';
import { TooltipHelp } from './TooltipHelp';

interface BusinessProfileModalProps {
  inbox: OfficialApiInbox;
  onClose: () => void;
}

export function BusinessProfileModal({ inbox, onClose }: BusinessProfileModalProps) {
  const { theme } = useTheme();
  
  // Estados do formulário
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<BusinessProfile | null>(null);
  
  const [about, setAbout] = useState('');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [vertical, setVertical] = useState<BusinessVertical>('OTHER');
  const [address, setAddress] = useState('');
  const [website, setWebsite] = useState('');
  
  // Estados para foto de perfil
  const [profilePictureUrl, setProfilePictureUrl] = useState<string | null>(null);
  const [newProfilePicture, setNewProfilePicture] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  
  // Estado para modal de preview
  const [showPreview, setShowPreview] = useState(false);

  // Estados para configuração de API (Templates com Mídia)
  const [appId, setAppId] = useState('');
  const [savingApiConfig, setSavingApiConfig] = useState(false);
  const [showAppId, setShowAppId] = useState(false); // Estado para controlar visibilidade do APP_ID

  // Carregar perfil atual
  useEffect(() => {
    loadProfile();
    // Carregar configuração de APP_ID salvo
    setAppId(inbox.providerConfig.appId || '');
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      console.log('📥 Carregando perfil do número:', inbox.providerConfig.phoneNumberId);
      
      const data = await profileApiService.getBusinessProfile(
        inbox.providerConfig.phoneNumberId
      );
      
      setProfile(data);
      
      // Preencher campos do formulário
      setAbout(data.about || '');
      setDescription(data.description || '');
      setEmail(data.email || '');
      setVertical(data.vertical || 'OTHER');
      setAddress(data.address || '');
      setWebsite(data.websites?.[0] || ''); // Pegar primeiro website
      setProfilePictureUrl(data.profile_picture_url || null);
      
      console.log('✅ Perfil carregado:', data);
    } catch (error: any) {
      console.error('❌ Erro ao carregar perfil:', error);
      toast.error(error.message || 'Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validações básicas
    if (about.trim().length === 0) {
      toast.error('O campo "Sobre" é obrigatório');
      return;
    }
    
    if (about.length > 139) {
      toast.error('Campo "Sobre" excede limite de 139 caracteres');
      return;
    }
    
    if (description.length > 512) {
      toast.error('Campo "Descrição" excede limite de 512 caracteres');
      return;
    }
    
    if (email.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error('Email inválido');
        return;
      }
    }

    // Validar endereço
    if (address.length > 256) {
      toast.error('Campo "Endereço" excede limite de 256 caracteres');
      return;
    }

    // Validar website
    if (website.trim().length > 0) {
      try {
        new URL(website);
        if (!website.startsWith('http://') && !website.startsWith('https://')) {
          toast.error('Website deve começar com http:// ou https://');
          return;
        }
      } catch {
        toast.error('Website inválido');
        return;
      }
    }

    try {
      setSaving(true);
      console.log('📤 Atualizando perfil...');
      
      const updateData: UpdateProfileData = {
        about: about.trim(),
        description: description.trim(),
        email: email.trim() || undefined,
        vertical,
        address: address.trim() || undefined,
        websites: website.trim() ? [website.trim()] : undefined
      };
      
      await profileApiService.updateBusinessProfile(
        inbox.providerConfig.phoneNumberId,
        updateData
      );
      
      toast.success('Perfil atualizado com sucesso! ✅');
      console.log('✅ Perfil atualizado');
      
      // Aguardar um pouco e fechar
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error: any) {
      console.error('❌ Erro ao atualizar perfil:', error);
      toast.error(error.message || 'Erro ao atualizar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving || uploadingPicture) return;
    onClose();
  };

  const handlePictureSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!file.type.startsWith('image/')) {
      toast.error('Apenas imagens são permitidas');
      return;
    }

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Imagem muito grande (máximo 5MB)');
      return;
    }

    setNewProfilePicture(file);

    // Criar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadPicture = async () => {
    if (!newProfilePicture) return;

    try {
      setUploadingPicture(true);
      console.log('📸 Fazendo upload de foto de perfil...');

      const newUrl = await profileApiService.uploadProfilePicture(
        inbox.providerConfig.phoneNumberId,
        newProfilePicture
      );

      setProfilePictureUrl(newUrl);
      setNewProfilePicture(null);
      setPreviewUrl(null);

      toast.success('Foto de perfil atualizada! ✅');
      console.log('✅ Foto atualizada:', newUrl);

    } catch (error: any) {
      console.error('❌ Erro ao fazer upload de foto:', error);
      
      // Verificar se o erro é por falta de APP_ID
      if (error.message?.includes('APP_ID não configurado')) {
        toast.error('⚠️ Configure o APP_ID da Meta primeiro na seção "Templates com Mídia"');
      } else {
        toast.error(error.message || 'Erro ao fazer upload de foto');
      }
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleCancelPicture = () => {
    setNewProfilePicture(null);
    setPreviewUrl(null);
  };

  const handleSaveApiConfig = async () => {
    // Validações
    if (appId.trim() === '') {
      toast.error('APP_ID é obrigatório');
      return;
    }

    if (!/^\d{15,16}$/.test(appId.trim())) {
      toast.error('APP_ID inválido. Deve ser um número de 15-16 dígitos');
      return;
    }

    try {
      setSavingApiConfig(true);
      console.log('💾 Salvando APP_ID...');

      // Usar apiService para salvar apenas o APP_ID
      const response = await apiService.post(
        `/resumable-media/config/${inbox.providerConfig.phoneNumberId}`,
        {
          appId: appId.trim()
        }
      );

      toast.success('✅ APP_ID salvo com sucesso!');
      console.log('✅ APP_ID salvo:', response);

    } catch (error: any) {
      console.error('❌ Erro ao salvar APP_ID:', error);
      toast.error(error.message || 'Erro ao salvar APP_ID');
    } finally {
      setSavingApiConfig(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div 
        className={`rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col ${
          theme === 'dark' ? 'bg-[#2A2A2A]' : 'bg-white'
        }`}
      >
        {/* Header */}
        <div className={`px-6 py-4 border-b ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-n-slate-12 flex items-center gap-2">
                <span>⚙️ Configurar Perfil Comercial</span>
                <TooltipHelp id="whatsapp_business_profile" />
              </h2>
              <p className="text-sm mt-1 text-n-slate-11">
                {inbox.name} • {inbox.phoneNumber}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPreview(true)}
                disabled={loading || saving}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  loading || saving
                    ? 'opacity-50 cursor-not-allowed'
                    : theme === 'dark'
                      ? 'bg-blue-600 hover:bg-blue-700 text-white'
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                👁️ Ver Preview
              </button>
              <button
                onClick={handleClose}
                disabled={saving}
                className={`p-2 rounded-lg transition-colors ${
                  saving
                    ? 'opacity-50 cursor-not-allowed'
                    : theme === 'dark'
                      ? 'hover:bg-gray-700 text-gray-400'
                      : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                Carregando perfil...
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Foto de Perfil */}
              <div className={`p-4 rounded-lg border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}>
                <label className={`block text-sm font-medium mb-3 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  📸 Foto de Perfil
                </label>

                <div className="flex items-start gap-4">
                  {/* Preview da foto */}
                  <div className="flex-shrink-0">
                    <div className={`w-24 h-24 rounded-full overflow-hidden border-2 ${
                      theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
                    }`}>
                      {previewUrl || profilePictureUrl ? (
                        <img
                          src={previewUrl || profilePictureUrl || ''}
                          alt="Foto de perfil"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className={`w-full h-full flex items-center justify-center ${
                          theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                        }`}>
                          <svg className={`w-12 h-12 ${
                            theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Controles */}
                  <div className="flex-1">
                    {newProfilePicture ? (
                      // Preview com botões de ação
                      <div>
                        <p className={`text-sm mb-2 ${
                          theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Nova foto selecionada: <strong>{newProfilePicture.name}</strong>
                        </p>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={handleUploadPicture}
                            disabled={uploadingPicture}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              uploadingPicture
                                ? 'bg-blue-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700'
                            } text-white`}
                          >
                            {uploadingPicture ? (
                              <span className="flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Enviando...
                              </span>
                            ) : (
                              '📤 Enviar Foto'
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={handleCancelPicture}
                            disabled={uploadingPicture}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                              uploadingPicture
                                ? 'opacity-50 cursor-not-allowed'
                                : theme === 'dark'
                                  ? 'bg-gray-700 hover:bg-gray-600 text-white'
                                  : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                            }`}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Input de upload
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePictureSelect}
                          className="hidden"
                          id="profile-picture-input"
                        />
                        <label
                          htmlFor="profile-picture-input"
                          className={`inline-block px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-colors ${
                            theme === 'dark'
                              ? 'bg-gray-700 hover:bg-gray-600 text-white'
                              : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                          }`}
                        >
                          📂 Escolher Imagem
                        </label>
                        <p className={`text-xs mt-2 ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          JPG, PNG ou GIF • Máximo 5MB
                        </p>
                        <p className={`text-xs ${
                          theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          Recomendado: 640x640 pixels
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Configurações de API (Templates com Mídia) */}
              <div className={`p-5 rounded-lg border-2 ${
                theme === 'dark' ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-300'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <label className={`block text-base font-semibold ${
                      theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
                    }`}>
                      🔧 Configuração do APP_ID (Templates com Mídia)
                    </label>
                    <p className={`text-xs mt-1 ${
                      theme === 'dark' ? 'text-blue-400/70' : 'text-blue-700/70'
                    }`}>
                      Necessário para fazer upload de mídia ao criar templates (imagem/vídeo/documento)
                    </p>
                  </div>
                  {appId && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                      ✓ Configurado
                    </span>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Campo: APP_ID */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}>
                      🆔 APP_ID da Meta
                    </label>
                    <div className="relative">
                      <input
                        type={showAppId ? "text" : "password"}
                        value={appId}
                        onChange={(e) => setAppId(e.target.value)}
                        placeholder="Ex: 1234567890123456"
                        className={`w-full px-4 py-2 pr-12 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm ${
                          theme === 'dark'
                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowAppId(!showAppId)}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded hover:bg-opacity-10 hover:bg-gray-500 transition-colors ${
                          theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
                        }`}
                        title={showAppId ? "Ocultar APP_ID" : "Mostrar APP_ID"}
                      >
                        {showAppId ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                            <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <p className={`text-xs mt-1 ${
                      theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                    }`}>
                      💡 Obtenha em <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">developers.facebook.com/apps</a>
                    </p>
                  </div>


                  {/* Botão Salvar Configuração de API */}
                  <button
                    type="button"
                    onClick={handleSaveApiConfig}
                    disabled={savingApiConfig}
                    className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
                      savingApiConfig
                        ? 'bg-blue-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                    } text-white`}
                  >
                    {savingApiConfig ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Salvando...
                      </span>
                    ) : (
                      '💾 Salvar APP_ID'
                    )}
                  </button>

                  {/* Alerta informativo */}
                  <div className={`p-3 rounded-lg ${
                    theme === 'dark' ? 'bg-yellow-900/20 border border-yellow-700' : 'bg-yellow-50 border border-yellow-200'
                  }`}>
                    <p className={`text-xs ${
                      theme === 'dark' ? 'text-yellow-300' : 'text-yellow-800'
                    }`}>
                      ⚠️ <strong>Importante:</strong> Configure o APP_ID apenas uma vez por número. 
                      É necessário para fazer upload de mídia ao criar templates com imagem, vídeo ou documento.
                    </p>
                  </div>
                </div>
              </div>

              {/* Nome de Exibição (Read-only) */}
              <div className={`p-4 rounded-lg border ${
                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  📛 Nome de exibição
                </label>
                <div className="flex items-center justify-between">
                  <p className={`text-base font-semibold ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    {inbox.name}
                  </p>
                  {profile?.name_status && profile.name_status !== 'NONE' && (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      profile.name_status === 'APPROVED'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : profile.name_status === 'PENDING'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {profile.name_status === 'APPROVED' ? '✓ Aprovado' :
                       profile.name_status === 'PENDING' ? '⏱️ Pendente' :
                       profile.name_status === 'REJECTED' ? '✗ Rejeitado' :
                       profile.name_status === 'EXPIRED' ? '⚠️ Expirado' : ''}
                    </span>
                  )}
                </div>
                <p className={`text-xs mt-2 ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                }`}>
                  ℹ️ O nome de exibição só pode ser alterado pelo Meta Business Manager e requer aprovação.
                </p>
              </div>
              
              {/* Campo: Sobre (About) */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  📝 Sobre sua empresa *
                </label>
                <textarea
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  placeholder="Descreva seu negócio em poucas palavras..."
                  rows={2}
                  maxLength={139}
                  required
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } ${about.length > 139 ? 'border-red-500' : ''}`}
                />
                <div className="flex justify-between mt-1">
                  <p className={`text-xs ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    Aparece no topo do perfil do WhatsApp
                  </p>
                  <p className={`text-xs ${
                    about.length > 139 
                      ? 'text-red-500 font-medium' 
                      : theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    {about.length}/139 caracteres
                  </p>
                </div>
              </div>

              {/* Campo: Descrição Detalhada */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  📄 Descrição Detalhada
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Conte mais sobre seus produtos, serviços e diferenciais..."
                  rows={4}
                  maxLength={512}
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } ${description.length > 512 ? 'border-red-500' : ''}`}
                />
                <div className="flex justify-between mt-1">
                  <p className={`text-xs ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    Informações adicionais sobre sua empresa
                  </p>
                  <p className={`text-xs ${
                    description.length > 512 
                      ? 'text-red-500 font-medium' 
                      : theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    {description.length}/512 caracteres
                  </p>
                </div>
              </div>

              {/* Campo: Categoria */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  🏢 Categoria do Negócio
                </label>
                <select
                  value={vertical}
                  onChange={(e) => setVertical(e.target.value as BusinessVertical)}
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  {BusinessVerticalOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className={`text-xs mt-1 ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                }`}>
                  Ajuda clientes a encontrar seu negócio
                </p>
              </div>

              {/* Campo: Email */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  📧 Email Comercial
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="contato@empresa.com"
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
                <p className={`text-xs mt-1 ${
                  theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                }`}>
                  Será visível publicamente no perfil
                </p>
              </div>

              {/* Campo: Website */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  🌐 Site
                </label>
                <input
                  type="url"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://empresa.com"
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                />
                <div className="flex justify-between mt-1">
                  <p className={`text-xs ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    Deve começar com http:// ou https://
                  </p>
                  <p className={`text-xs ${
                    website.length > 256 
                      ? 'text-red-500 font-medium' 
                      : theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    {website.length}/256 caracteres
                  </p>
                </div>
              </div>

              {/* Campo: Endereço */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  📍 Endereço
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Insira o endereço comercial"
                  maxLength={256}
                  className={`w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    theme === 'dark'
                      ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  } ${address.length > 256 ? 'border-red-500' : ''}`}
                />
                <div className="flex justify-between mt-1">
                  <p className={`text-xs ${
                    theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    Endereço físico do seu negócio
                  </p>
                  <p className={`text-xs ${
                    address.length > 256 
                      ? 'text-red-500 font-medium' 
                      : theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
                  }`}>
                    {address.length}/256 caracteres
                  </p>
                </div>
              </div>

            </form>
          )}
        </div>

        {/* Footer */}
        {!loading && (
          <div className={`px-6 py-4 border-t flex justify-end gap-3 ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                saving
                  ? 'opacity-50 cursor-not-allowed'
                  : theme === 'dark'
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
              }`}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                saving
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white`}
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Salvando...
                </span>
              ) : (
                '💾 Salvar Alterações'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Modal de Preview */}
      {showPreview && (
        <ProfilePreviewModal
          inbox={inbox}
          profile={profile}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

